import type { Entities_Player_ProgressSummary_Response } from '@suntex.fim/api-client';
import {
  $GradeLevel,
  $LearningStage,
  GradeLevel,
  LearningStage,
} from '@suntex.fim/enums';

import { ModuleTagging, StrictNodeModel } from '../types';

import { computeModuleProgress } from './computeStarValue';
import { getEffectiveChildren } from './getEffectiveChildren';
import { withFromParam } from './withFromParam';

/** Silver star progress is complete at 1 (gold progress beyond that is ignored). */
const SILVER_PROGRESS = 1;

export interface StagesOfLearningLink {
  /** `module` links open the module itself; `hub` links open the hub at a node. */
  readonly kind: 'module' | 'hub';
  readonly href: string;
}

export interface SolNodeProgress {
  readonly key: string;
  readonly label: string;
  /** Silver star progress, from 0 to 1. */
  readonly progress: number;
  readonly link: StagesOfLearningLink;
}

export interface SolStageProgress extends SolNodeProgress {
  readonly stage: LearningStage;
  readonly color: number;
  readonly emphasized: boolean;
  readonly children: readonly SolNodeProgress[];
}

export interface SolLevelProgress {
  readonly key: string;
  readonly gradeLevel: GradeLevel;
  readonly label: string;
  readonly color: number;
  readonly link: StagesOfLearningLink;
  readonly stages: readonly SolStageProgress[];
}

interface BuildContext {
  readonly progressSummary:
    | Entities_Player_ProgressSummary_Response
    | undefined;
  readonly moduleTagging: readonly ModuleTagging[];
  readonly isModuleEnabled: (moduleSlug: string) => boolean;
  /** Rewrites a module href (e.g. for preview mode). */
  readonly getHref: (href: string) => string;
  /** Builds a URL that opens the hub at the given node path. */
  readonly getHubHref: (path: string) => string;
}

/**
 * Builds the view model for the Stages of Learning progress report from the
 * standardized `stages-of-learning` node (levels -> stages -> modules).
 *
 * - Levels that are not enabled are omitted (same rule as the canvas hub).
 * - Progress is silver star progress (0-1); a branch's progress is the average
 *   of its enabled children's progress.
 * - Stage 2 (Got It) of the level matching `gradeLevel` is emphasized.
 */
export function buildStagesOfLearningProgress({
  solNode,
  gradeLevel,
  ...ctx
}: BuildContext & {
  readonly solNode: StrictNodeModel;
  readonly gradeLevel: GradeLevel | undefined;
}): SolLevelProgress[] {
  return solNode.children
    .filter((levelNode) => !levelNode.hidden && isNodeEnabled(levelNode, ctx))
    .map((levelNode) => {
      const level = $GradeLevel.describe(
        $GradeLevel.fromSlug(levelNode.tag ?? ''),
      );
      return {
        key: levelNode.key,
        gradeLevel: level.value,
        label: level.label,
        color: levelNode.color,
        link: buildLink(levelNode, levelNode.key, ctx),
        stages: getEffectiveChildren(levelNode, ctx.moduleTagging).map(
          (stageNode) => {
            const stage = $LearningStage.describe(
              $LearningStage.fromSlug(stageNode.tag ?? ''),
            );
            const children = getEnabledChildren(stageNode, ctx).map(
              (child) => ({
                key: child.key,
                label: getNodeLabel(child),
                progress: computeSilverProgress(child, ctx),
                link: buildLink(child, stageNode.key, ctx),
              }),
            );
            return {
              key: stageNode.key,
              stage: stage.value,
              label: `Stage ${stage.value}: ${stage.label}`,
              color: stageNode.color,
              emphasized:
                level.value === gradeLevel &&
                stage.value === LearningStage.GotIt,
              progress: average(children.map((c) => c.progress)),
              link: buildLink(stageNode, stageNode.key, ctx),
              children,
            };
          },
        ),
      };
    });
}

/**
 * Mirrors the canvas hub's enabled check (`Radial.checkEnabled`): a module
 * node is enabled by its slug, otherwise children are combined using the
 * node's `recursiveLogic` (default `OR`), and an empty node is enabled.
 */
function isNodeEnabled(node: StrictNodeModel, ctx: BuildContext): boolean {
  if (node.moduleSlug) {
    return ctx.isModuleEnabled(node.moduleSlug);
  }
  const children = getEffectiveChildren(node, ctx.moduleTagging);
  if (children.length === 0) {
    return true;
  }
  return node.recursiveLogic === 'AND'
    ? children.every((c) => isNodeEnabled(c, ctx))
    : children.some((c) => isNodeEnabled(c, ctx));
}

function getEnabledChildren(
  node: StrictNodeModel,
  ctx: BuildContext,
): StrictNodeModel[] {
  return getEffectiveChildren(node, ctx.moduleTagging).filter((c) =>
    isNodeEnabled(c, ctx),
  );
}

function computeSilverProgress(
  node: StrictNodeModel,
  ctx: BuildContext,
): number {
  if (getEffectiveChildren(node, ctx.moduleTagging).length === 0) {
    return ctx.progressSummary
      ? Math.min(
          computeModuleProgress(ctx.progressSummary, node),
          SILVER_PROGRESS,
        )
      : 0;
  }
  return average(
    getEnabledChildren(node, ctx).map((c) => computeSilverProgress(c, ctx)),
  );
}

function average(values: readonly number[]): number {
  return values.length === 0
    ? 0
    : values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * Nodes with an href link to the module (annotated with the hub path to
 * return to, like the canvas hub); all other nodes link into the hub.
 */
function buildLink(
  node: StrictNodeModel,
  fromKey: string,
  ctx: BuildContext,
): StagesOfLearningLink {
  return node.href
    ? { kind: 'module', href: withFromParam(ctx.getHref(node.href), fromKey) }
    : { kind: 'hub', href: ctx.getHubHref(node.key) };
}

function getNodeLabel(node: StrictNodeModel): string {
  return typeof node.label === 'string'
    ? node.label.replace(/\s+/g, ' ').trim()
    : node.label.aria;
}
